/**
 * Thin fetch wrapper for the customer-portal UI's backend calls.
 *
 * - `credentials: 'include'` so the `__Host-orboto_portal_session` cookie (set
 *   by /v1/portal/auth/login) rides on every authenticated request.
 *   Same-origin in prod (account.orboto.io serves /portal/* AND
 *   /v1/portal/* from the same Fastify instance) so this is mostly a
 *   no-op, but the Vite dev proxy on :4322 still needs it.
 * - Throws `ApiError` on non-2xx. Callers in the AuthContext catch and
 *   inspect `.status` to discriminate 401 (re-prompt for credentials),
 *   428 (TOTP required, step-up), 429 (rate-limited), etc.
 * - JSON-only — every portal endpoint we touch from React produces JSON.
 * - CSRF (OCP-125): for every mutating method reads the
 *   `__Host-orboto_portal_csrf` cookie set on signup/login + replays it in
 *   the `X-CSRF-Token` header. Mismatch → 403 csrf_invalid.
 * - CSRF drift recovery (OCP-208): in-flight sessions that pre-date
 *   the CSRF deploy hit 403 csrf_missing on the first mutation. The
 *   server's `maybeIssueMissingCsrfCookie` hook mints the cookie on
 *   the NEXT GET — so this helper transparently pings /v1/portal/auth/me
 *   on 403 csrf_invalid / csrf_missing, then retries the original
 *   request once. If the retry also fails, the error surfaces normally.
 */
import { CSRF_HEADER_NAME, PORTAL_CSRF_COOKIE_NAME } from '@orboto/ocp-shared';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API error ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  /**
   * Pull a stable, machine-readable error code out of the response
   * body. The portal API uses `{ error: 'snake_case_token', ... }` —
   * matches the admin surface and lets the UI render specific copy for
   * known states (e.g. `totp_required`, `email_not_verified`) without
   * relying on HTTP-status alone.
   */
  get code(): string | null {
    if (this.body && typeof this.body === 'object' && 'error' in this.body) {
      const e = (this.body as { error: unknown }).error;
      return typeof e === 'string' ? e : null;
    }
    return null;
  }
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';

  const doFetch = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (isMutatingMethod(method)) {
      const csrf = readCookie(PORTAL_CSRF_COOKIE_NAME);
      if (csrf) headers[CSRF_HEADER_NAME] = csrf;
    }
    return fetch(path, {
      method,
      credentials: 'include',
      headers: Object.keys(headers).length > 0 ? headers : undefined,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  };

  let res = await doFetch();

  // CSRF drift recovery (OCP-208) — see header comment.
  if (res.status === 403 && isMutatingMethod(method)) {
    const peekText = await res.text();
    const peekBody: unknown = peekText ? safeParseJson(peekText) : undefined;
    const errKey = readErrorKey(peekBody);
    if (errKey === 'csrf_invalid' || errKey === 'csrf_missing') {
      try {
        await fetch('/v1/portal/auth/me', { credentials: 'include' });
      } catch {
        // Ping failed (network blip) — fall through to the retry.
      }
      res = await doFetch();
    } else {
      throw new ApiError(403, peekBody);
    }
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body: unknown = text ? safeParseJson(text) : undefined;

  if (!res.ok) {
    throw new ApiError(res.status, body);
  }
  return body as T;
}

function readErrorKey(body: unknown): string | null {
  if (body && typeof body === 'object' && 'error' in body) {
    const e = (body as { error: unknown }).error;
    return typeof e === 'string' ? e : null;
  }
  return null;
}

function isMutatingMethod(method: string): boolean {
  return method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie;
  if (!raw) return null;
  // Skip empty same-name placeholders (jsdom under tests can leave a
  // deleted-but-not-collected entry alongside a freshly-set real
  // value). In real browsers same-name cookies are replaced; either
  // way the last non-empty value wins.
  const pairs = raw.split(/;\s*/);
  const prefix = `${name}=`;
  let lastNonEmpty: string | null = null;
  for (const pair of pairs) {
    if (pair.startsWith(prefix)) {
      const value = decodeURIComponent(pair.slice(prefix.length));
      if (value) lastNonEmpty = value;
    }
  }
  return lastNonEmpty;
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
